import {
    assertUnreachable,
    getHumanReadableCronExpression,
    isEmailTarget,
    isGoogleChatTarget,
    isMsTeamsTarget,
    isSchedulerGsheetsOptions,
    isSlackTarget,
    SchedulerFormat,
    SchedulerRunStatus,
} from '@lightdash/common';
import {
    Anchor,
    Badge,
    Box,
    Group,
    Stack,
    Text,
    Tooltip,
    useMantineTheme,
} from '@mantine-8/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
    IconAlertCircle,
    IconArrowDown,
    IconArrowsSort,
    IconArrowUp,
    IconChartBar,
    IconCheck,
    IconClock,
    IconCodeDots,
    IconLayoutDashboard,
    IconMail,
    IconRadar,
    IconRun,
    IconTextCaption,
    IconUser,
} from '@tabler/icons-react';
import {
    MantineReactTable,
    useMantineReactTable,
    type MRT_ColumnDef,
    type MRT_SortingState,
    type MRT_Virtualizer,
} from 'mantine-react-table';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type UIEvent,
} from 'react';
import { Link, useSearchParams } from 'react-router';
import { usePaginatedSchedulers } from '../../features/scheduler/hooks/useScheduler';
import { useSchedulerFilters } from '../../features/scheduler/hooks/useSchedulerFilters';
import { useIsTruncated } from '../../hooks/useIsTruncated';
import { useProject } from '../../hooks/useProject';
import GSheetsSvg from '../../svgs/google-sheets.svg?react';
import GoogleChatSvg from '../../svgs/googlechat.svg?react';
import SlackSvg from '../../svgs/slack.svg?react';
import MantineIcon from '../common/MantineIcon';
import ReassignSchedulerOwnerModal from './ReassignSchedulerOwnerModal';
import SchedulersViewActionMenu from './SchedulersViewActionMenu';
import { SchedulersViewTab } from './SchedulersViewConstants';
import {
    getSchedulerIcon,
    getSchedulerLink,
    type SchedulerItem,
} from './SchedulersViewUtils';
import { SchedulerTopToolbar } from './SchedulerTopToolbar';

interface SchedulersTableProps {
    projectUuid?: string;
    getSlackChannelName: (channelId: string) => string | null;
    onSlackChannelIdsChange?: (channelIds: string[]) => void;
    /**
     * When true, shows schedulers for the current user only (user scope).
     * When false, shows all schedulers for the project (project scope).
     */
    isUserScope?: boolean;
}

const fetchSize = 50;

const getRunStatusConfig = (status: SchedulerRunStatus) => {
    switch (status) {
        case SchedulerRunStatus.COMPLETED:
            return { color: 'green', icon: IconCheck, label: 'Completed' };
        case SchedulerRunStatus.PARTIAL_FAILURE:
            return {
                color: 'yellow',
                icon: IconAlertCircle,
                label: 'Partial failure',
            };
        case SchedulerRunStatus.FAILED:
            return { color: 'red', icon: IconAlertCircle, label: 'Failed' };
        case SchedulerRunStatus.RUNNING:
            return { color: 'blue', icon: IconRun, label: 'Running' };
        case SchedulerRunStatus.SCHEDULED:
            return { color: 'gray', icon: IconClock, label: 'Scheduled' };
        default:
            return assertUnreachable(status, 'Unknown scheduler run status');
    }
};

const SchedulersTable: FC<SchedulersTableProps> = ({
    projectUuid,
    getSlackChannelName,
    onSlackChannelIdsChange,
    isUserScope = false,
}) => {
    const theme = useMantineTheme();
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const rowVirtualizerInstanceRef =
        useRef<MRT_Virtualizer<HTMLDivElement, HTMLTableRowElement>>(null);
    const [, setSearchParams] = useSearchParams();

    const {
        search,
        selectedFormats,
        selectedResourceType,
        selectedCreatedByUserUuids,
        selectedDestinations,
        sortField,
        sortDirection,
        setSearch,
        setSelectedFormats,
        setSelectedResourceType,
        setSelectedCreatedByUserUuids,
        setSelectedDestinations,
        setSorting,
        hasActiveFilters,
        resetFilters,
        apiFilters,
    } = useSchedulerFilters();

    // Debounce filters to avoid too many API calls
    const debouncedFilters = useMemo(() => {
        return { search, apiFilters, sortField, sortDirection };
    }, [search, apiFilters, sortField, sortDirection]);

    const [debouncedSearchAndFilters] = useDebouncedValue(
        debouncedFilters,
        300,
    );

    const { data, fetchNextPage, isError, isFetching, isLoading } =
        usePaginatedSchedulers({
            projectUuid,
            paginateArgs: { page: 1, pageSize: fetchSize },
            searchQuery: debouncedSearchAndFilters.search,
            sortBy: debouncedSearchAndFilters.sortField,
            sortDirection: debouncedSearchAndFilters.sortDirection,
            filters: debouncedSearchAndFilters.apiFilters,
            includeLatestRun: true,
            isUserScope,
        });

    const flatData = useMemo<SchedulerItem[]>(
        () => data?.pages.flatMap((page) => page.data) ?? [],
        [data],
    );

    const totalDBRowCount = data?.pages?.[0]?.pagination?.totalResults ?? 0;
    const totalFetched = flatData.length;
    const { data: project } = useProject(projectUuid);

    // Temporary workaround to resolve a memoization issue with react-mantine-table.
    const [tableData, setTableData] = useState<SchedulerItem[]>([]);
    useEffect(() => {
        setTableData(flatData);
    }, [flatData]);

    // Reassign owner modal state
    const [reassignModalOpen, setReassignModalOpen] = useState(false);
    const [schedulerUuidsToReassign, setSchedulerUuidsToReassign] = useState<
        string[]
    >([]);
    const [excludedUserUuid, setExcludedUserUuid] = useState<
        string | undefined
    >(undefined);
    const [isBulkReassign, setIsBulkReassign] = useState(false);

    // Single scheduler reassign (from context menu)
    const handleReassignOwner = useCallback(
        (schedulerUuid: string, ownerUuid: string | undefined) => {
            setSchedulerUuidsToReassign([schedulerUuid]);
            setExcludedUserUuid(ownerUuid);
            setIsBulkReassign(false);
            setReassignModalOpen(true);
        },
        [],
    );

    const handleReassignModalClose = useCallback(() => {
        setReassignModalOpen(false);
        setSchedulerUuidsToReassign([]);
        setExcludedUserUuid(undefined);
        setIsBulkReassign(false);
    }, []);

    // Extract unique Slack channel IDs from loaded schedulers and report them
    useEffect(() => {
        if (!onSlackChannelIdsChange) return;

        const channelIds = new Set<string>();
        flatData.forEach((scheduler) => {
            scheduler.targets.forEach((target) => {
                if (isSlackTarget(target)) {
                    channelIds.add(target.channel);
                }
            });
        });
        onSlackChannelIdsChange(Array.from(channelIds));
    }, [flatData, onSlackChannelIdsChange]);

    // Callback to fetch more data when scrolling
    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } =
                    containerRefElement;
                if (
                    scrollHeight - scrollTop - clientHeight < 400 &&
                    !isFetching &&
                    totalFetched < totalDBRowCount
                ) {
                    void fetchNextPage();
                }
            }
        },
        [fetchNextPage, isFetching, totalFetched, totalDBRowCount],
    );

    // Scroll to top when sorting or filters change
    useEffect(() => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = 0;
        }
    }, [debouncedSearchAndFilters]);

    // Check on mount if table needs initial fetch
    useEffect(() => {
        fetchMoreOnBottomReached(tableContainerRef.current);
    }, [fetchMoreOnBottomReached]);

    const sorting = useMemo<MRT_SortingState>(
        () => [{ id: sortField, desc: sortDirection === 'desc' }],
        [sortField, sortDirection],
    );

    const handleSortingChange = useCallback(
        (
            updaterOrValue:
                | MRT_SortingState
                | ((old: MRT_SortingState) => MRT_SortingState),
        ) => {
            const newSorting =
                typeof updaterOrValue === 'function'
                    ? updaterOrValue(sorting)
                    : updaterOrValue;

            if (newSorting.length > 0) {
                const { id, desc } = newSorting[0];
                setSorting(id as 'name' | 'createdAt', desc ? 'desc' : 'asc');
            }
        },
        [sorting, setSorting],
    );

    const columns: MRT_ColumnDef<SchedulerItem>[] = useMemo(() => {
        const baseColumns: MRT_ColumnDef<SchedulerItem>[] = [
            {
                accessorKey: 'name',
                header: 'Name',
                enableSorting: true,
                size: 300,
                Header: ({ column }) => (
                    <Group gap="two">
                        <MantineIcon icon={IconTextCaption} color="ldGray.6" />
                        {column.columnDef.header}
                    </Group>
                ),
                Cell: ({ row }) => {
                    const isTruncated = useIsTruncated<HTMLDivElement>();
                    const item = row.original;
                    const format = () => {
                        switch (item.format) {
                            case SchedulerFormat.CSV:
                                return 'CSV';
                            case SchedulerFormat.XLSX:
                                return 'XLSX';
                            case SchedulerFormat.IMAGE:
                                return 'Image';
                            case SchedulerFormat.GSHEETS:
                                return 'Google Sheets';
                            case SchedulerFormat.PDF:
                                return 'PDF';
                            default:
                                return assertUnreachable(
                                    item.format,
                                    'Unknown scheduler format',
                                );
                        }
                    };

                    // For user scope, use projectUuid from the item itself
                    const itemProjectUuid = isUserScope
                        ? item.projectUuid
                        : projectUuid;

                    return (
                        <Group wrap="nowrap">
                            {getSchedulerIcon(item)}
                            <Stack gap="two">
                                <Anchor
                                    component={Link}
                                    to={getSchedulerLink(item, itemProjectUuid)}
                                    target="_blank"
                                >
                                    <Tooltip
                                        fz="xs"
                                        label={
                                            <Stack gap="one" fz="xs">
                                                <Text c="ldGray.5" fz="xs">
                                                    Schedule type:{' '}
                                                    <Text
                                                        c="white"
                                                        span
                                                        fz="xs"
                                                    >
                                                        {format()}
                                                    </Text>
                                                </Text>
                                                {!isUserScope && (
                                                    <Text c="ldGray.5" fz="xs">
                                                        Created by:{' '}
                                                        <Text
                                                            c="white"
                                                            span
                                                            fz="xs"
                                                        >
                                                            {item.createdByName ||
                                                                'n/a'}
                                                        </Text>
                                                    </Text>
                                                )}
                                            </Stack>
                                        }
                                    >
                                        <Text
                                            fw={600}
                                            fz="xs"
                                            lineClamp={1}
                                            style={{
                                                overflowWrap: 'anywhere',
                                                cursor: 'pointer',
                                            }}
                                            ref={isTruncated.ref}
                                        >
                                            {item.name}
                                        </Text>
                                    </Tooltip>
                                </Anchor>
                                {item.savedChartName ? (
                                    <Group gap="two">
                                        <MantineIcon
                                            icon={IconChartBar}
                                            color="ldGray.6"
                                            size={12}
                                            strokeWidth={1.5}
                                        />
                                        <Text fz="xs" c="ldGray.6">
                                            {item.savedChartName}
                                        </Text>
                                    </Group>
                                ) : item.savedSqlName ? (
                                    <Group gap="two">
                                        <MantineIcon
                                            icon={IconCodeDots}
                                            color="ldGray.6"
                                            size={12}
                                            strokeWidth={1.5}
                                        />
                                        <Text fz="xs" c="ldGray.6">
                                            {item.savedSqlName}
                                        </Text>
                                    </Group>
                                ) : item.dashboardName ? (
                                    <Group gap="two">
                                        <MantineIcon
                                            icon={IconLayoutDashboard}
                                            color="ldGray.6"
                                            strokeWidth={1.5}
                                            size={12}
                                        />
                                        <Text fz="xs" c="ldGray.6">
                                            {item.dashboardName}
                                        </Text>
                                    </Group>
                                ) : null}
                            </Stack>
                        </Group>
                    );
                },
            },
        ];

        // Owner column - only for project scope
        if (!isUserScope) {
            baseColumns.push({
                accessorKey: 'createdByName',
                header: 'Owner',
                enableSorting: false,
                size: 150,
                Header: ({ column }) => (
                    <Group gap="two" wrap="nowrap">
                        <MantineIcon icon={IconUser} color="ldGray.6" />
                        {column.columnDef.header}
                    </Group>
                ),
                Cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <Text fz="xs" c="ldGray.6">
                            {item.createdByName || 'Unknown'}
                        </Text>
                    );
                },
            });
        }

        // Project column - only for user scope
        if (isUserScope) {
            baseColumns.push({
                accessorKey: 'projectName',
                header: 'Project',
                enableSorting: false,
                size: 150,
                Header: ({ column }) => (
                    <Group gap="two" wrap="nowrap">
                        <MantineIcon
                            icon={IconLayoutDashboard}
                            color="ldGray.6"
                        />
                        {column.columnDef.header}
                    </Group>
                ),
                Cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <Text fz="xs" c="ldGray.6">
                            {item.projectName || 'Unknown'}
                        </Text>
                    );
                },
            });
        }

        // Last Run column
        baseColumns.push({
            accessorKey: 'lastRunStatus',
            header: 'Last Run',
            enableSorting: false,
            size: 160,
            Header: ({ column }) => (
                <Group gap="two" wrap="nowrap">
                    <MantineIcon icon={IconRun} color="ldGray.6" />
                    {column.columnDef.header}
                </Group>
            ),
            Cell: ({ row }) => {
                const item = row.original;
                const latestRun = item.latestRun;

                if (!latestRun) {
                    return (
                        <Text fz="xs" c="ldGray.6">
                            No runs yet
                        </Text>
                    );
                }

                const statusConfig = getRunStatusConfig(latestRun.runStatus);

                // For project scope, badge is clickable to view run history
                if (!isUserScope) {
                    return (
                        <Tooltip
                            label={
                                <Stack gap="xxs">
                                    <Text fz="xs">
                                        Last run:{' '}
                                        {new Date(
                                            latestRun.scheduledTime,
                                        ).toLocaleString()}
                                    </Text>
                                    {latestRun.logCounts && (
                                        <Text fz="xs" c="ldGray.5">
                                            {latestRun.logCounts.completed}{' '}
                                            completed,{' '}
                                            {latestRun.logCounts.error} failed
                                        </Text>
                                    )}
                                    <Text fz="xs" c="ldGray.4" fs="italic">
                                        Click to view run history
                                    </Text>
                                </Stack>
                            }
                        >
                            <Badge
                                component="button"
                                type="button"
                                variant="light"
                                size="sm"
                                radius="sm"
                                tt="capitalize"
                                fw={400}
                                color={statusConfig.color}
                                leftSection={
                                    <MantineIcon
                                        icon={statusConfig.icon}
                                        size="xs"
                                    />
                                }
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setSearchParams({
                                        tab: SchedulersViewTab.RUN_HISTORY,
                                        schedulerUuid: item.schedulerUuid,
                                    });
                                }}
                            >
                                {statusConfig.label}
                            </Badge>
                        </Tooltip>
                    );
                }

                // For user scope, badge is not clickable
                return (
                    <Tooltip
                        label={
                            <Stack gap="xxs">
                                <Text fz="xs">
                                    Last run:{' '}
                                    {new Date(
                                        latestRun.scheduledTime,
                                    ).toLocaleString()}
                                </Text>
                                {latestRun.logCounts && (
                                    <Text fz="xs" c="ldGray.5">
                                        {latestRun.logCounts.completed}{' '}
                                        completed, {latestRun.logCounts.error}{' '}
                                        failed
                                    </Text>
                                )}
                            </Stack>
                        }
                    >
                        <Badge
                            variant="light"
                            size="sm"
                            radius="sm"
                            tt="capitalize"
                            fw={400}
                            color={statusConfig.color}
                            leftSection={
                                <MantineIcon
                                    icon={statusConfig.icon}
                                    size="xs"
                                />
                            }
                        >
                            {statusConfig.label}
                        </Badge>
                    </Tooltip>
                );
            },
        });

        // Destinations column
        baseColumns.push({
            accessorKey: 'destinations',
            header: 'Destinations',
            enableSorting: false,
            size: 140,
            Header: ({ column }) => (
                <Group gap="two" wrap="nowrap">
                    <MantineIcon icon={IconRadar} color="ldGray.6" />
                    {column.columnDef.header}
                </Group>
            ),
            Cell: ({ row }) => {
                const item = row.original;
                const currentTargets = item.targets.filter(
                    (target) => target.schedulerUuid === item.schedulerUuid,
                );
                let emails: string[] = [];
                let slackChannels: string[] = [];
                let msTeamsTargets: string[] = [];
                let googleChatTargets: string[] = [];
                currentTargets.map((t) => {
                    if (isSlackTarget(t)) {
                        return slackChannels.push(
                            getSlackChannelName(t.channel) || t.channel,
                        );
                    } else if (isGoogleChatTarget(t)) {
                        return googleChatTargets.push(t.googleChatWebhook);
                    } else if (isMsTeamsTarget(t)) {
                        return msTeamsTargets.push(t.webhook);
                    } else if (isEmailTarget(t)) {
                        return emails.push(t.recipient);
                    }
                    return undefined;
                });

                return (
                    <Group gap="xxs">
                        {emails.length > 0 && (
                            <Tooltip
                                label={emails.map((email, i) => (
                                    <Text fz="xs" key={i}>
                                        {email}
                                    </Text>
                                ))}
                            >
                                <Group gap="two">
                                    <MantineIcon
                                        icon={IconMail}
                                        color="ldGray.6"
                                    />
                                    <Text fz="xs" c="ldGray.6">
                                        {slackChannels.length > 0
                                            ? 'Email,'
                                            : 'Email'}
                                    </Text>
                                </Group>
                            </Tooltip>
                        )}
                        {slackChannels.length > 0 && (
                            <Tooltip
                                label={slackChannels.map((channel, i) => (
                                    <Text fz="xs" key={i}>
                                        {channel}
                                    </Text>
                                ))}
                            >
                                <Group gap="two">
                                    <SlackSvg
                                        style={{
                                            margin: '5px 2px',
                                            width: '12px',
                                            height: '12px',
                                            filter: 'grayscale(100%)',
                                        }}
                                    />
                                    <Text fz="xs" c="ldGray.6">
                                        Slack
                                    </Text>
                                </Group>
                            </Tooltip>
                        )}
                        {item.format === SchedulerFormat.GSHEETS &&
                            isSchedulerGsheetsOptions(item.options) && (
                                <Tooltip label={item.options.gdriveName}>
                                    <Group gap="two">
                                        <GSheetsSvg
                                            style={{
                                                margin: '5px 2px',
                                                width: '12px',
                                                height: '12px',
                                                filter: 'grayscale(100%)',
                                            }}
                                        />
                                        <Anchor
                                            fz="xs"
                                            c="ldGray.6"
                                            href={item.options.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{
                                                textDecoration: 'underline',
                                            }}
                                        >
                                            Google Sheets
                                        </Anchor>
                                    </Group>
                                </Tooltip>
                            )}
                        {googleChatTargets.length > 0 && (
                            <Tooltip
                                label={googleChatTargets.map((webhook, i) => (
                                    <Text fz="xs" key={i}>
                                        {webhook}
                                    </Text>
                                ))}
                            >
                                <Group gap="two">
                                    <GoogleChatSvg
                                        style={{
                                            margin: '5px 2px',
                                            width: '12px',
                                            height: '12px',
                                            filter: 'grayscale(100%)',
                                        }}
                                    />
                                    <Text fz="xs" c="ldGray.6">
                                        Google Chat
                                    </Text>
                                </Group>
                            </Tooltip>
                        )}
                        {item.format !== SchedulerFormat.GSHEETS &&
                            slackChannels.length === 0 &&
                            emails.length === 0 &&
                            msTeamsTargets.length === 0 &&
                            googleChatTargets.length === 0 && (
                                <Text fz="xs" c="ldGray.6">
                                    No destinations
                                </Text>
                            )}
                    </Group>
                );
            },
        });

        // Frequency column
        baseColumns.push({
            accessorKey: 'frequency',
            header: 'Frequency',
            enableSorting: false,
            size: 150,
            Header: ({ column }) => (
                <Group gap="two">
                    <MantineIcon icon={IconClock} color="ldGray.6" />
                    {column.columnDef.header}
                </Group>
            ),
            Cell: ({ row }) => {
                const item = row.original;
                return (
                    <Text fz="xs" c="ldGray.6">
                        {getHumanReadableCronExpression(
                            item.cron,
                            item.timezone ||
                                item.projectSchedulerTimezone ||
                                project?.schedulerTimezone ||
                                'UTC',
                        )}
                    </Text>
                );
            },
        });

        // Created column
        baseColumns.push({
            accessorKey: 'createdAt',
            header: 'Created',
            enableSorting: true,
            size: 130,
            Header: ({ column }) => (
                <Group gap="two" wrap="nowrap">
                    <MantineIcon icon={IconClock} color="ldGray.6" />
                    {column.columnDef.header}
                </Group>
            ),
            Cell: ({ row }) => {
                const item = row.original;
                return (
                    <Text fz="xs" c="ldGray.6">
                        {new Date(item.createdAt).toLocaleDateString()}
                    </Text>
                );
            },
        });

        // Actions column
        baseColumns.push({
            id: 'actions',
            header: '',
            enableSorting: false,
            enableResizing: false,
            size: 50,
            Cell: ({ row }) => {
                const item = row.original;
                // For user scope, use projectUuid from the item itself
                const itemProjectUuid = isUserScope
                    ? item.projectUuid
                    : projectUuid;

                return (
                    <Box
                        component="div"
                        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                    >
                        <SchedulersViewActionMenu
                            item={item}
                            projectUuid={itemProjectUuid}
                            onReassignOwner={handleReassignOwner}
                        />
                    </Box>
                );
            },
        });

        return baseColumns;
    }, [
        isUserScope,
        project,
        projectUuid,
        getSlackChannelName,
        setSearchParams,
        handleReassignOwner,
    ]);

    const table = useMantineReactTable({
        columns,
        data: tableData,
        enableColumnResizing: true,
        enableRowNumbers: false,
        enablePagination: false,
        enableFilters: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableGlobalFilterModes: false,
        enableSorting: true,
        enableRowVirtualization: true,
        manualSorting: true,
        onSortingChange: handleSortingChange,
        enableTopToolbar: true,
        enableBottomToolbar: false,
        // Row selection only for project scope (bulk reassign)
        enableRowSelection: !isUserScope,
        ...(isUserScope
            ? {}
            : {
                  mantineSelectCheckboxProps: { size: 'xs' },
                  mantineSelectAllCheckboxProps: { size: 'xs' },
                  displayColumnDefOptions: {
                      'mrt-row-select': {
                          size: 20,
                          minSize: 20,
                          maxSize: 20,
                          enableResizing: false,
                      },
                  },
              }),
        getRowId: (row) => row.schedulerUuid,
        mantinePaperProps: {
            shadow: undefined,
            style: {
                border: `1px solid ${theme.colors.ldGray[2]}`,
                borderRadius: theme.spacing.sm,
                boxShadow: theme.shadows.subtle,
                display: 'flex',
                flexDirection: 'column',
            },
        },
        mantineTableHeadRowProps: {
            sx: {
                boxShadow: 'none',
                'th > div > div:last-child': {
                    top: -10,
                    right: -5,
                },
                'th > div > div:last-child > .mantine-Divider-root': {
                    border: 'none',
                },
            },
        },
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { maxHeight: 'calc(100dvh - 420px)' },
            onScroll: (event: UIEvent<HTMLDivElement>) =>
                fetchMoreOnBottomReached(event.target as HTMLDivElement),
        },
        mantineTableProps: {
            highlightOnHover: true,
            withColumnBorders: Boolean(tableData.length),
        },
        mantineTableHeadCellProps: (props) => {
            const isLastColumn =
                props.table.getAllColumns().indexOf(props.column) ===
                props.table.getAllColumns().length - 1;

            const isAnyColumnResizing = props.table
                .getAllColumns()
                .some((c) => c.getIsResizing());
            const canResize = props.column.getCanResize();

            return {
                bg: 'ldGray.0',
                h: '3xl',
                pos: 'relative',
                style: {
                    userSelect: 'none',
                    justifyContent: 'center',
                    padding: `${theme.spacing.xs} ${theme.spacing.xl}`,
                    borderTop: `1px solid ${theme.colors.ldGray[2]}`,
                    borderBottom: `1px solid ${theme.colors.ldGray[2]}`,
                    borderRight: props.column.getIsResizing()
                        ? `2px solid ${theme.colors.blue[3]}`
                        : `1px solid ${
                              isLastColumn
                                  ? 'transparent'
                                  : theme.colors.ldGray[2]
                          }`,
                    borderLeft: 'none',
                },
                sx: {
                    '&:hover': canResize
                        ? {
                              borderRight: !isAnyColumnResizing
                                  ? `2px solid ${theme.colors.blue[3]} !important`
                                  : undefined,
                              transition: `border-right ${theme.other.transitionDuration}ms ${theme.other.transitionTimingFunction}`,
                          }
                        : {},
                },
            };
        },
        mantineTableBodyCellProps: () => {
            return {
                h: 72,
                style: {
                    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                    borderRight: 'none',
                    borderLeft: 'none',
                    borderBottom: `1px solid ${theme.colors.ldGray[2]}`,
                    borderTop: 'none',
                },
            };
        },
        renderTopToolbar: ({ table: tableInstance }) => {
            const selectedRows = tableInstance
                .getFilteredSelectedRowModel()
                .flatRows.map((row) => row.original);

            const handleBulkReassign = () => {
                setSchedulerUuidsToReassign(
                    selectedRows.map((row) => row.schedulerUuid),
                );
                setExcludedUserUuid(undefined);
                setIsBulkReassign(true);
                setReassignModalOpen(true);
            };

            return (
                <SchedulerTopToolbar
                    projectUuid={projectUuid ?? ''}
                    search={search}
                    setSearch={setSearch}
                    selectedFormats={selectedFormats}
                    setSelectedFormats={setSelectedFormats}
                    selectedResourceType={selectedResourceType}
                    setSelectedResourceType={setSelectedResourceType}
                    selectedCreatedByUserUuids={selectedCreatedByUserUuids}
                    setSelectedCreatedByUserUuids={
                        setSelectedCreatedByUserUuids
                    }
                    selectedDestinations={selectedDestinations}
                    setSelectedDestinations={setSelectedDestinations}
                    isFetching={isFetching || isLoading}
                    currentResultsCount={totalFetched}
                    hasActiveFilters={hasActiveFilters}
                    onClearFilters={resetFilters}
                    selectedCount={selectedRows.length}
                    onBulkReassign={handleBulkReassign}
                    hideCreatedByFilter={isUserScope}
                    hideBulkReassign={isUserScope}
                />
            );
        },
        icons: {
            IconArrowsSort: () => (
                <MantineIcon icon={IconArrowsSort} size="md" color="ldGray.5" />
            ),
            IconSortAscending: () => (
                <MantineIcon icon={IconArrowUp} size="md" color="blue.6" />
            ),
            IconSortDescending: () => (
                <MantineIcon icon={IconArrowDown} size="md" color="blue.6" />
            ),
        },
        rowVirtualizerInstanceRef,
        rowVirtualizerProps: { overscan: 10 },
        state: {
            sorting,
            isLoading,
            showAlertBanner: isError,
            showProgressBars: isFetching,
            density: 'md',
        },
    });

    const handleReassignSuccess = useCallback(() => {
        if (isBulkReassign) {
            table.resetRowSelection();
        }
    }, [isBulkReassign, table]);

    // Check if any schedulers being reassigned are GSHEETS format
    const hasGsheetsSchedulers = useMemo(() => {
        return schedulerUuidsToReassign.some((uuid) => {
            const scheduler = flatData.find((s) => s.schedulerUuid === uuid);
            return scheduler?.format === SchedulerFormat.GSHEETS;
        });
    }, [schedulerUuidsToReassign, flatData]);

    const reassignProjectUuid = useMemo(() => {
        if (projectUuid) return projectUuid;
        if (schedulerUuidsToReassign.length > 0) {
            const scheduler = flatData.find(
                (s) => s.schedulerUuid === schedulerUuidsToReassign[0],
            );
            return scheduler?.projectUuid;
        }
        return undefined;
    }, [projectUuid, schedulerUuidsToReassign, flatData]);

    return (
        <>
            <MantineReactTable table={table} />
            {reassignProjectUuid && (
                <ReassignSchedulerOwnerModal
                    opened={reassignModalOpen}
                    onClose={handleReassignModalClose}
                    projectUuid={reassignProjectUuid}
                    schedulerUuids={schedulerUuidsToReassign}
                    excludedUserUuid={excludedUserUuid}
                    onSuccess={handleReassignSuccess}
                    hasGsheetsSchedulers={hasGsheetsSchedulers}
                />
            )}
        </>
    );
};

export default SchedulersTable;
